import React, { useState, useEffect, useSyncExternalStore } from 'react';
import { useTour } from '@reactour/tour';
import styles from './CANpark.module.css';
import CanTab from 'containers/cantabs/components/CanTab';
import { useMediaQuery, useTheme } from '@mui/material';
import {
	getCanTabState,
	setCanTabState,
} from 'containers/cantabs/libs/useLocalStorage';
import CANparkBar from 'containers/pages/CANpark/components/navbar/CANparkBar';
import CANparkHomeTab from 'containers/pages/CANpark/components/home/CANparkHomeTab';
import { ActiveVehicleProvider, useActiveVehicle } from 'containers/pages/CANpark/context/ActiveVehicleProvider';
import { CANparkRoleProvider } from 'containers/pages/CANpark/context/CANparkRoleProvider';
import { useTicketingTourSync } from 'components/onboarding/tour/useTicketingTourSync';
import { getActiveTourKey } from 'components/onboarding/tour/activeTour';
import {
	subscribeTicketCapture,
	getTicketCaptureMode,
	setTicketDrawerBack,
} from 'components/onboarding/tour/ticketingCaptureState';

export enum CANparkTabs {
	Home = 'Home',
}

type CANparkTabComponents = {
	[key in CANparkTabs]: React.ReactNode;
};

function useCanTabs() {
	const [isCanTabOpen, setIsCanTabOpen] = useState<Boolean>(true);
	const theme = useTheme();
	const isMd = useMediaQuery(theme.breakpoints.down('md'));

	const onOpenCanTab = () => {
		const canTabSettings = getCanTabState();
		canTabSettings.menuState = 'open';
		setCanTabState(canTabSettings);
		setIsCanTabOpen(true);
	};

	const onCloseCanTab = () => {
		const canTabSettings = getCanTabState();
		canTabSettings.menuState = 'closed';
		setCanTabState(canTabSettings);
		setIsCanTabOpen(false);
	};

	return { isCanTabOpen, isMd, onOpenCanTab, onCloseCanTab };
}

function CANpark() {
	// This component now only wraps other components with the AdminProvider
	if (process.env.NODE_ENV === 'production') {
		document.title = 'CANpark - Ticketing System';
	} else {
		document.title = 'Park V3.2 (Local)';
	}
	return (
		<CANparkRoleProvider>
			<ActiveVehicleProvider>
				<CANparkContent />
			</ActiveVehicleProvider>
		</CANparkRoleProvider>
	);
}

function CANparkContent() {
	const { isCanTabOpen, onOpenCanTab, onCloseCanTab } = useCanTabs();
	const [activeTab, setActiveTab] = useState<CANparkTabs>(CANparkTabs.Home);

	// Drives the CANpark ticketing walkthrough from the live drawer/pipeline state.
	const {
		showDrawer,
		stage,
		pipeline,
		openModal,
		setOpenModal,
		goBack,
		canGoBack,
		closeDrawer,
	} = useActiveVehicle();
	const captureMode = useSyncExternalStore(
		subscribeTicketCapture,
		getTicketCaptureMode,
	);
	useTicketingTourSync(showDrawer, stage, pipeline, captureMode, openModal);

	// Lets the tour's Back button on in-drawer steps go back a stage (or close the
	// drawer at the first stage); the sync hook then follows the stage change. If
	// the Uppy upload modal is open (the UPLOADING step), Back just closes it —
	// otherwise the sync hook, which forces UPLOADING while the modal is open,
	// would immediately override a plain step-back.
	useEffect(() => {
		setTicketDrawerBack(() => {
			if (openModal) {
				setOpenModal(false);
				return;
			}
			if (canGoBack) goBack();
			else closeDrawer();
		});
		return () => setTicketDrawerBack(null);
	}, [openModal, setOpenModal, canGoBack, goBack, closeDrawer]);

	// Collapse the side menu when the ticketing tour starts so the walkthrough
	// isn't fighting the CANtab drawer for space / attention.
	const { isOpen: tourOpen } = useTour();
	useEffect(() => {
		if (tourOpen && getActiveTourKey() === 'canpark_issue_ticket') {
			onCloseCanTab();
		}
		// onCloseCanTab is recreated each render; intentionally keyed on tourOpen.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tourOpen]);

	const handleTabChange = (tab: CANparkTabs) => {
		setActiveTab(tab);
	};

	const tabComponents: CANparkTabComponents = {
		[CANparkTabs.Home]: <CANparkHomeTab />,
	};

	return (
		<main className={styles.shared_layout}>
			<CanTab
				open={isCanTabOpen}
				onOpen={onOpenCanTab}
				onClose={onCloseCanTab}
				isMd={true}
			/>
			<section className={styles.right_section}>
				<CANparkBar
					activeTab={activeTab}
					onMenuClick={onOpenCanTab}
					handleTabChange={handleTabChange}
				/>
				{/* Always render the tabs first but control its visibility. This way the state inside is preserved */}
				{Object.entries(tabComponents).map(([key, component]) => (
					<div
						key={key}
						className={styles.content}
						style={{
							display: activeTab === key ? 'flex' : 'none',
						}}
					>
						{component}
					</div>
				))}
			</section>
		</main>
	);
}

export default CANpark;
